import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, Radio, Plus, Check, Music, BarChart3, Loader2, RefreshCw, ExternalLink, Sparkles, AlertCircle, ThumbsDown, Download, Calendar as CalendarIcon, ClipboardCopy, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from './config';
import { runAiJob } from './utils/aiJobs';

const LiveTrendsView = ({ eventoId: eventoIdProp, onAddToPlaylist, hideTrendsTab = false }) => {
  const [activeTab, setActiveTab] = useState(hideTrendsTab ? 'predictor' : 'trends');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Selector de evento (sólo se usa cuando no llega prop eventoIdProp)
  const [availableEvents, setAvailableEvents] = useState([]);
  const [selectedEventoId, setSelectedEventoId] = useState('');
  const eventoId = eventoIdProp || selectedEventoId;
  
  // Trends state
  const [trends, setTrends] = useState([]);
  const [trendsStats, setTrendsStats] = useState({
    total: 0,
    en_biblioteca: 0,
    no_tienes: 0,
    ultima_actualizacion: ''
  });
  // Modo degradado: true cuando el backend devolvió el fallback estático porque
  // las fuentes externas (YT Music/Shazam/Apple) están caídas. El banner avisa
  // al DJ para que no se fíe ciegamente de las tendencias mostradas.
  const [isDegraded, setIsDegraded] = useState(false);
  
  // Predictor state
  const [prediction, setPrediction] = useState(null);
  const [genreStats, setGenreStats] = useState([]);
  
  // Load trends on mount
  useEffect(() => {
    loadTrends();
    // Si no llega evento por prop, cargar lista de eventos para el dropdown
    if (!eventoIdProp) {
      loadAvailableEvents();
    }
    // Mount-only effect — loadTrends/loadAvailableEvents son estables (useCallback)
  }, []); // eslint-disable-line
  
  // Al cambiar el evento seleccionado, limpiar la predicción previa
  useEffect(() => {
    setPrediction(null);
  }, [eventoId]);
  
  const loadAvailableEvents = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/playlists`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const events = response.data.playlists || response.data || [];
      // Ordenar por fecha descendente (próximos primero) y limitar a los últimos 50
      const sorted = [...events]
        .filter((e) => e && e.id)
        .sort((a, b) => {
          const da = (a.fecha_evento || a.fecha || '').toString();
          const db = (b.fecha_evento || b.fecha || '').toString();
          return db.localeCompare(da);
        })
        .slice(0, 50);
      setAvailableEvents(sorted);
    } catch (e) {
      console.error('[LiveTrendsView] Error cargando eventos:', e);
    }
  }, []);
  
  const loadTrends = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/live-trends?limit=30`);
      setTrends(response.data.tendencias || []);
      setTrendsStats({
        total: response.data.total,
        en_biblioteca: response.data.en_tu_biblioteca,
        no_tienes: response.data.no_tienes,
        ultima_actualizacion: response.data.ultima_actualizacion
      });
      setIsDegraded(Boolean(response.data.degraded));
    } catch (error) {
      console.error('Error loading trends:', error);
      toast.error('Error al cargar tendencias');
    } finally {
      setLoading(false);
    }
  };
  
  const loadTrendsForEvent = async () => {
    if (!eventoId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/live-trends/for-event/${eventoId}?limit=20`);
      setTrends(response.data.tendencias_recomendadas || []);
      toast.success(`Tendencias filtradas para: ${response.data.tipo_evento}`);
    } catch (error) {
      console.error('Error loading event trends:', error);
      toast.error('Error al cargar tendencias del evento');
    } finally {
      setLoading(false);
    }
  };
  
  const refreshTrends = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API_URL}/live-trends/refresh`);
      await loadTrends();
      toast.success('Tendencias actualizadas');
    } catch (error) {
      // Puede fallar si no es admin, intentar reload normal
      await loadTrends();
    } finally {
      setRefreshing(false);
    }
  };
  
  const addToLibrary = async (trend) => {
    try {
      const response = await axios.post(`${API_URL}/live-trends/add-to-library`, trend);
      if (response.data.success) {
        toast.success(
          <div className="flex flex-col gap-1">
            <span className="font-semibold">Añadida a Pendientes Denon</span>
            <span className="text-sm text-slate-400">{trend.title} - {trend.artist}</span>
          </div>
        );
        // Update local state
        setTrends(prev => prev.map(t => 
          t.title === trend.title && t.artist === trend.artist
            ? { ...t, en_biblioteca: true, biblioteca_id: response.data.song_id }
            : t
        ));
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      console.error('Error adding to library:', error);
      toast.error('Error al añadir a biblioteca');
    }
  };
  
  const loadPrediction = async () => {
    if (!eventoId) {
      toast.error('Selecciona un evento primero');
      return;
    }
    
    setLoading(true);
    try {
      const response = await runAiJob({
        startUrl: `${API_URL}/success-predictor/ai-analysis/start?evento_id=${eventoId}`,
        statusBase: `${API_URL}/success-predictor/jobs`,
        body: {},
      });
      setPrediction(response.data);
    } catch (error) {
      console.error('Error loading prediction:', error);
      toast.error('Error al cargar predicción');
    } finally {
      setLoading(false);
    }
  };
  
  const loadGenreStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/success-predictor/genre-stats?tipo_evento=boda`);
      setGenreStats(response.data.estadisticas || []);
    } catch (error) {
      console.error('Error loading genre stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const [copyingNote, setCopyingNote] = useState(false);
  const copyPredictionAsNote = async () => {
    if (!prediction || !eventoId) {
      toast.error('No hay predicción para copiar');
      return;
    }
    setCopyingNote(true);
    try {
      const ai = prediction.analisis_ia || {};
      const lines = ['🤖 **Predictor IA — Análisis del evento**', ''];
      if (ai.perfil_musical) {
        lines.push(`**Perfil musical:** ${ai.perfil_musical}`);
        lines.push('');
      }
      if (Array.isArray(ai.canciones_recomendadas) && ai.canciones_recomendadas.length > 0) {
        lines.push('✅ **Canciones recomendadas (tu biblioteca):**');
        ai.canciones_recomendadas.forEach((s) => {
          const motivo = s.motivo ? ` — ${s.motivo}` : '';
          lines.push(`• ${s.titulo} - ${s.artista}${motivo}`);
        });
        lines.push('');
      }
      if (Array.isArray(prediction.sugerencias_spotify) && prediction.sugerencias_spotify.length > 0) {
        lines.push('🎧 **Descubre en Spotify:**');
        prediction.sugerencias_spotify.forEach((s) => {
          lines.push(`• ${s.titulo} - ${s.artista}`);
        });
        lines.push('');
      }
      if (Array.isArray(ai.canciones_a_evitar) && ai.canciones_a_evitar.length > 0) {
        lines.push('🚫 **Evitar en este evento:**');
        ai.canciones_a_evitar.forEach((s) => {
          const motivo = s.motivo ? ` (${s.motivo})` : '';
          lines.push(`• ${s.titulo} - ${s.artista}${motivo}`);
        });
        lines.push('');
      }
      if (Array.isArray(ai.consejos_evento) && ai.consejos_evento.length > 0) {
        lines.push('💡 **Consejos:**');
        ai.consejos_evento.forEach((c) => lines.push(`• ${c}`));
        lines.push('');
      }
      if (ai.resumen) {
        lines.push(`📝 *${ai.resumen}*`);
      }
      const content = lines.join('\n').trim();

      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/playlists/${eventoId}/notes`,
        {
          content,
          visible_to_client: false,
          tipo: 'idea',
          priority: 'media',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('📋 Predictor copiado como nota — visible en LiveMode');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error guardando como nota');
    } finally {
      setCopyingNote(false);
    }
  };
  
  // Format date
  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleString('es-ES', { 
      day: '2-digit', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <Card className="bg-slate-900 border-slate-700">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Radio className="w-5 h-5 text-red-400 animate-pulse" />
              {hideTrendsTab ? 'Análisis IA · Estadísticas' : 'En Vivo - Tendencias España'}
            </CardTitle>
            <CardDescription className="text-slate-400">
              {hideTrendsTab
                ? 'Predicción de éxito por evento + estadísticas por género'
                : 'Qué está sonando ahora en eventos similares'}
            </CardDescription>
          </div>
          {!hideTrendsTab && (
            <Button
              variant="outline"
              size="sm"
              onClick={refreshTrends}
              disabled={refreshing}
              className="border-slate-600 text-slate-300"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          )}
        </div>
      </CardHeader>
      
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Banner "modo degradado" (fuera de TabsContent): visible independientemente
              de la pestaña activa. Radix Tabs solo monta el TabsContent activo, así que
              si viviera dentro de <TabsContent value="trends"> quedaría oculto cuando
              TrendsPage pasa hideTrendsTab={true} (activeTab=predictor). */}
          {isDegraded && (
            <div
              className="flex items-start gap-3 mb-4 rounded-lg border border-amber-500/30 bg-amber-950/40 p-3"
              data-testid="trends-degraded-banner"
              role="alert"
            >
              <WifiOff className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
              <div className="flex-1 text-sm">
                <div className="font-semibold text-amber-200">Tendencias del respaldo</div>
                <div className="text-amber-300/80">
                  Las fuentes en tiempo real (YT Music · Shazam · Apple Music) no
                  responden ahora mismo. Ver esto como orientación, no como top actual.
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={loadTrends}
                disabled={loading}
                data-testid="trends-degraded-retry"
                className="shrink-0 border-amber-500/40 bg-amber-950/60 text-amber-200 hover:bg-amber-900/60 hover:text-amber-100"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reintentar'}
              </Button>
            </div>
          )}
          <TabsList className="bg-slate-800 mb-4">
            {!hideTrendsTab && (
              <TabsTrigger value="trends" className="data-[state=active]:bg-cyan-600">
                <TrendingUp className="w-4 h-4 mr-1" />
                Tendencias
              </TabsTrigger>
            )}
            <TabsTrigger value="predictor" className="data-[state=active]:bg-purple-600">
              <Sparkles className="w-4 h-4 mr-1" />
              Predictor IA
            </TabsTrigger>
            <TabsTrigger value="stats" className="data-[state=active]:bg-green-600">
              <BarChart3 className="w-4 h-4 mr-1" />
              Estadísticas
            </TabsTrigger>
          </TabsList>
          
          {/* TAB: TENDENCIAS */}
          <TabsContent value="trends" className="mt-0">
            {/* Stats bar */}
            <div className="flex items-center gap-4 mb-4 text-sm">
              <div className="flex items-center gap-1 text-slate-400">
                <Music className="w-4 h-4" />
                {trendsStats.total} canciones
              </div>
              <div className="flex items-center gap-1 text-green-400">
                <Check className="w-4 h-4" />
                {trendsStats.en_biblioteca} ya tienes
              </div>
              <div className="flex items-center gap-1 text-orange-400">
                <Plus className="w-4 h-4" />
                {trendsStats.no_tienes} nuevas
              </div>
              {eventoId && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadTrendsForEvent}
                  className="text-cyan-400 hover:text-cyan-300 ml-auto"
                >
                  Filtrar para mi evento
                </Button>
              )}
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {trends.map((trend, idx) => (
                    <div 
                      key={`${trend.title}-${idx}`}
                      className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                        trend.en_biblioteca 
                          ? 'bg-green-500/10 border border-green-500/30' 
                          : 'bg-slate-800 hover:bg-slate-700'
                      }`}
                    >
                      {/* Position */}
                      <div className="w-8 text-center">
                        <span className={`text-lg font-bold ${
                          trend.position <= 3 ? 'text-yellow-400' :
                          trend.position <= 10 ? 'text-cyan-400' :
                          'text-slate-500'
                        }`}>
                          {trend.position}
                        </span>
                      </div>
                      
                      {/* Album art */}
                      {trend.album_art ? (
                        <img 
                          src={trend.album_art} 
                          alt={trend.title}
                          className="w-10 h-10 rounded object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded bg-slate-700 flex items-center justify-center">
                          <Music className="w-5 h-5 text-slate-500" />
                        </div>
                      )}
                      
                      {/* Song info */}
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-medium truncate">
                          {trend.title}
                        </div>
                        <div className="text-sm text-slate-400 truncate">
                          {trend.artist}
                        </div>
                      </div>
                      
                      {/* Actions - Spotify + YouTube + Denon */}
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {/* Spotify link */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-7 h-7 bg-green-600/20 text-green-400 hover:bg-green-600/40 border border-green-600/30"
                          onClick={() => {
                            const url = trend.spotify_url || 
                              `https://open.spotify.com/search/${encodeURIComponent(trend.title + ' ' + trend.artist)}`;
                            window.open(url, '_blank');
                          }}
                          title="Abrir en Spotify"
                          data-testid={`live-trend-spotify-${idx}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>

                        {/* YouTube link */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-7 h-7 bg-red-600/20 text-red-400 hover:bg-red-600/40 border border-red-600/30"
                          onClick={() => {
                            const url = `https://www.youtube.com/results?search_query=${encodeURIComponent(trend.artist + ' ' + trend.title)}`;
                            window.open(url, '_blank');
                          }}
                          title="Buscar en YouTube"
                          data-testid={`live-trend-youtube-${idx}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>

                        {trend.en_biblioteca ? (
                          <Badge className="bg-green-600/20 text-green-400 border-green-500/50 text-xs px-1.5">
                            <Check className="w-3 h-3" />
                          </Badge>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => addToLibrary(trend)}
                            className="bg-cyan-600/20 text-cyan-400 hover:bg-cyan-600/40 border border-cyan-600/30 h-7 px-2 text-xs"
                            data-testid={`live-trend-denon-${idx}`}
                          >
                            <Download className="w-3.5 h-3.5 mr-1" />
                            Denon
                          </Button>
                        )}
                      </div>
                      
                      {/* Genre badge - hidden on small screens */}
                      {trend.genero_detectado && (
                        <Badge variant="outline" className="text-xs text-slate-400 border-slate-600 hidden lg:flex">
                          {trend.genero_detectado}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
            
            {trendsStats.ultima_actualizacion && (
              <div className="text-xs text-slate-500 mt-3 text-center">
                Última actualización: {formatDate(trendsStats.ultima_actualizacion)}
              </div>
            )}
          </TabsContent>
          
          {/* TAB: PREDICTOR IA */}
          <TabsContent value="predictor" className="mt-0">
            {/* Selector de evento — sólo si el componente NO recibió uno por prop */}
            {!eventoIdProp && (
              <div className="mb-4">
                <label className="text-xs text-slate-400 mb-1 flex items-center gap-1">
                  <CalendarIcon className="w-3 h-3" /> Evento a analizar
                </label>
                <Select value={selectedEventoId} onValueChange={setSelectedEventoId}>
                  <SelectTrigger
                    className="bg-slate-800 border-slate-600 text-white"
                    data-testid="predictor-event-select"
                  >
                    <SelectValue placeholder="Selecciona un evento…" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-600 text-white max-h-[50vh]">
                    {availableEvents.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-slate-400">
                        Cargando eventos…
                      </div>
                    ) : (
                      availableEvents.map((ev) => {
                        const fecha = ev.fecha_evento || ev.fecha || '';
                        const nombre = ev.nombre_novios || ev.name || ev.nombre || 'Sin nombre';
                        const tipo = ev.tipo_evento ? ` (${ev.tipo_evento.split(',')[0].slice(0, 20)})` : '';
                        const label = fecha
                          ? `${fecha.slice(0, 10)} · ${nombre}${tipo}`
                          : `${nombre}${tipo}`;
                        return (
                          <SelectItem
                            key={ev.id}
                            value={ev.id}
                            data-testid={`predictor-event-option-${ev.id}`}
                          >
                            {label}
                          </SelectItem>
                        );
                      })
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {!eventoId ? (
              <div className="text-center py-12 text-slate-400">
                <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Selecciona un evento para ver predicciones</p>
              </div>
            ) : (
              <>
                <Button
                  onClick={loadPrediction}
                  disabled={loading}
                  className="w-full bg-purple-600 hover:bg-purple-500 mb-4"
                  data-testid="analyze-playlist-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Analizar y Sugerir Canciones con IA
                </Button>
                
                {prediction && (
                  <ScrollArea className="h-[450px]">
                    <div className="space-y-4 pr-3">
                      {/* Perfil del evento */}
                      {prediction.analisis_ia?.perfil_musical && (
                        <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-purple-300 mb-2">
                            <Music className="w-4 h-4" />
                            <span className="font-medium">Perfil Musical del Evento</span>
                          </div>
                          <p className="text-sm text-slate-200">{prediction.analisis_ia.perfil_musical}</p>
                          <div className="flex flex-wrap gap-2 mt-2 text-xs">
                            <span className="px-2 py-1 bg-slate-700 rounded text-slate-300">
                              {prediction.tipo_evento}
                            </span>
                            {prediction.edad_media && (
                              <span className="px-2 py-1 bg-slate-700 rounded text-slate-300">
                                Edad: {prediction.edad_media}
                              </span>
                            )}
                            {prediction.gustos_cliente && (
                              <span className="px-2 py-1 bg-cyan-700/50 rounded text-cyan-300">
                                Gustos: {prediction.gustos_cliente}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Canciones Recomendadas de tu Biblioteca */}
                      {prediction.analisis_ia?.canciones_recomendadas?.length > 0 && (
                        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-green-400 mb-3">
                            <Check className="w-4 h-4" />
                            <span className="font-medium">Canciones de Tu Biblioteca</span>
                            <Badge className="ml-auto bg-green-600/30 text-green-300 text-xs">
                              {prediction.analisis_ia.canciones_recomendadas.length} sugeridas
                            </Badge>
                          </div>
                          <div className="space-y-2">
                            {prediction.analisis_ia.canciones_recomendadas.map((song, idx) => (
                              <div 
                                key={idx}
                                className="flex items-start gap-3 p-2 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors"
                              >
                                <div className="w-6 h-6 rounded bg-green-600/30 flex items-center justify-center text-green-400 text-xs font-bold flex-shrink-0">
                                  {idx + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-white font-medium text-sm truncate">
                                    {song.titulo}
                                  </div>
                                  <div className="text-slate-400 text-xs truncate">{song.artista}</div>
                                  {song.motivo && (
                                    <div className="text-green-400/80 text-xs mt-1 italic">
                                      → {song.motivo}
                                    </div>
                                  )}
                                </div>
                                {song.en_biblioteca && onAddToPlaylist && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-green-400 hover:bg-green-500/20 h-7 px-2"
                                    onClick={() => onAddToPlaylist({ 
                                      id: song.song_id, 
                                      title: song.titulo, 
                                      artist: song.artista 
                                    })}
                                  >
                                    <Plus className="w-3 h-3 mr-1" />
                                    Añadir
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Sugerencias de Spotify (NO en tu biblioteca) */}
                      {prediction.sugerencias_spotify?.length > 0 && (
                        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-emerald-400 mb-3">
                            <TrendingUp className="w-4 h-4" />
                            <span className="font-medium">Descubre en Spotify</span>
                            <Badge className="ml-auto bg-emerald-600/30 text-emerald-300 text-xs">
                              No están en tu biblioteca
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-400 mb-3">
                            Canciones populares que podrían encajar con este evento
                          </p>
                          <div className="space-y-2">
                            {prediction.sugerencias_spotify.map((song, idx) => (
                              <div 
                                key={idx}
                                className="flex items-center gap-3 p-2 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors"
                              >
                                {song.imagen ? (
                                  <img 
                                    src={song.imagen} 
                                    alt={song.titulo}
                                    className="w-10 h-10 rounded object-cover flex-shrink-0"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded bg-slate-700 flex items-center justify-center flex-shrink-0">
                                    <Music className="w-5 h-5 text-slate-500" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="text-white font-medium text-sm truncate">
                                    {song.titulo}
                                  </div>
                                  <div className="text-slate-400 text-xs truncate">{song.artista}</div>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {song.popularidad > 0 && (
                                    <span className="text-xs text-amber-400 px-2 py-0.5 bg-amber-500/20 rounded">
                                      {song.popularidad}%
                                    </span>
                                  )}
                                  {song.spotify_url && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="w-8 h-8 text-green-400 hover:bg-green-500/20"
                                      onClick={() => window.open(song.spotify_url, '_blank')}
                                    >
                                      <ExternalLink className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-cyan-400 hover:bg-cyan-500/20 h-7 px-2"
                                    onClick={() => addToLibrary({
                                      title: song.titulo,
                                      artist: song.artista,
                                      spotify_url: song.spotify_url,
                                      genero_detectado: ''
                                    })}
                                  >
                                    <Plus className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Canciones a evitar */}
                      {prediction.analisis_ia?.canciones_a_evitar?.length > 0 && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-red-400 mb-2">
                            <ThumbsDown className="w-4 h-4" />
                            <span className="font-medium">Evitar en este evento</span>
                          </div>
                          <div className="space-y-1">
                            {prediction.analisis_ia.canciones_a_evitar.map((song, idx) => (
                              <div key={idx} className="text-sm text-slate-300">
                                <span className="text-red-400">✕</span> {song.titulo} - {song.artista}
                                {song.motivo && <span className="text-slate-500 text-xs ml-2">({song.motivo})</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Consejos */}
                      {prediction.analisis_ia?.consejos_evento?.length > 0 && (
                        <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-cyan-400 mb-2">
                            <Sparkles className="w-4 h-4" />
                            <span className="font-medium">Consejos para el evento</span>
                          </div>
                          <ul className="text-sm text-slate-300 space-y-1">
                            {prediction.analisis_ia.consejos_evento.map((c, i) => (
                              <li key={i}>• {c}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Resumen */}
                      {prediction.analisis_ia?.resumen && (
                        <div className="bg-slate-800 rounded-lg p-3 mt-2">
                          <p className="text-sm text-slate-300 italic">
                            "{prediction.analisis_ia.resumen}"
                          </p>
                        </div>
                      )}

                      {/* Copiar como Notas — guardar análisis para ver en LiveMode */}
                      <Button
                        onClick={copyPredictionAsNote}
                        disabled={copyingNote}
                        className="w-full bg-cyan-600 hover:bg-cyan-700 text-white mt-2"
                        data-testid="predictor-copy-as-note-btn"
                      >
                        {copyingNote ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</>
                        ) : (
                          <><ClipboardCopy className="w-4 h-4 mr-2" /> 📋 Copiar Análisis como Notas</>
                        )}
                      </Button>
                    </div>
                  </ScrollArea>
                )}
              </>
            )}
          </TabsContent>
          
          {/* TAB: ESTADÍSTICAS */}
          <TabsContent value="stats" className="mt-0">
            <Button
              onClick={loadGenreStats}
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-500 mb-4"
              data-testid="load-genre-stats-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <BarChart3 className="w-4 h-4 mr-2" />
              )}
              Cargar Estadísticas de Géneros
            </Button>
            
            {genreStats.length > 0 && (
              <ScrollArea className="h-[350px]">
                <div className="space-y-2">
                  {genreStats.map((stat, idx) => (
                    <div 
                      key={stat.genero}
                      className="flex items-center gap-3 p-3 bg-slate-800 rounded-lg"
                    >
                      <span className="text-xl">{stat.emoji}</span>
                      
                      <div className="flex-1">
                        <div className="text-white font-medium">{stat.genero}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-2">
                          <span>{stat.canciones_en_biblioteca} en biblioteca</span>
                          {stat.uso_real && stat.uso_real.eventos_usado > 0 && (
                            <span className="text-cyan-400">
                              • Usado en {stat.uso_real.eventos_usado} eventos
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className={`text-lg font-bold ${
                          stat.tasa_exito_estimada >= 80 ? 'text-green-400' :
                          stat.tasa_exito_estimada >= 60 ? 'text-yellow-400' :
                          stat.tasa_exito_estimada >= 40 ? 'text-orange-400' :
                          'text-red-400'
                        }`}>
                          {stat.tasa_exito_estimada}%
                        </div>
                        <div className="text-xs text-slate-500">tasa éxito</div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
            
            {genreStats.length === 0 && !loading && (
              <div className="text-center py-8 text-slate-400">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Carga las estadísticas para ver qué géneros funcionan mejor</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default LiveTrendsView;
